import React, { useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import Button from '@mlb/react-patterns/components/Button/button.tsx';
import { media } from '@mlb/react-patterns/style/helpers/media.ts';
import COLORS from '@mlb/react-patterns/style/universal.color';

import { Tracking } from 'bdvp-tracking';
import { useDictionary } from 'bdvp-common/src/hooks/config';

import Wrapper from '../ModalViewWrapper';
import Body from '../ModalBody';
import FooterComponent from '../ModalFooter';
import ThumbnailField from './ThumbnailField';
import TitleField from './TitleField';

const CreateReel = styled(Button)`
    width: 180px;
`;

const Footer = styled(FooterComponent)`
    justify-content: center;
`;

const LegalMessage = styled.p`
    color: ${COLORS.mlbGrey3};
    font-size: 11px;
    margin: 18px 0 0;
    line-height: normal;
    letter-spacing: 0.14px;
    ${media.mdmin`
        margin: 20px 0 0;
        font-size: 14px;
        letter-spacing: 0.18px;
    `}
`;

const defaultLegalMessage
    = 'Find and share your finished reels on Your Reels page personalized with your first name or MLB.com profile nickname. You can manage your nickname by visiting the Account section of MLB.com.';

const CreationForm = (props) => {

    const {
        reel,
        lastModalNode,
        validateReel,
        createReel,
        formState,
        setFormState,
    } = props;

    const {
        legal_message: legalMessage = defaultLegalMessage,
        create_reel_btn: createReelBtn = 'Create Reel',
    } = useDictionary();

    const [
        isValidating,
        setIsValidating,
    ] = useState(false);

    const handleChange = (event) => {

        const { name, value } = event.target;
        setFormState({
            ...formState,
            [name]: value,
            error: name === 'reelTitle' ? '' : formState.error,
        });

    };

    const handleSubmit = async(event) => {

        event.preventDefault();

        setIsValidating(true);

        const reelRequest = {
            reelTitle: formState.reelTitle,
            thumbnailUrl: formState.thumbnailUrl,
            reelClips: reel.reelClips.map(({
                __typename,
                mediaId,
                title,
                ...goodProps
            }) => ({
                ...goodProps,
            })),
        };

        const {
            valid = false,
            errors = [],
        } = await validateReel(reelRequest);

        if(valid) {

            return createReel(reelRequest);

        }

        setIsValidating(false);

        return setFormState({
            ...formState,
            error: errors[0],
        });

    };

    const isDisabled = formState.reelTitle.length < 1 || isValidating;

    return (
        <Wrapper as={'form'} onSubmit={handleSubmit}>
            <Body>
                <ThumbnailField
                    reel={reel}
                    formState={formState}
                    handleChange={handleChange}
                />
                <TitleField
                    formState={formState}
                    handleChange={handleChange}
                    isLastNode={isDisabled}
                    lastModalNode={lastModalNode}
                />
            </Body>
            <Footer>
                <CreateReel
                    {...!isDisabled && { ref: lastModalNode }}
                    type={'submit'}
                    disabled={isDisabled}
                    size={'md'}
                    label={createReelBtn}
                    styleName={'link-blue'}
                    handlers={{
                        onClick: () => {

                            Tracking.trackReelCreationFormSubmit();

                        },
                    }}
                />
                <LegalMessage>
                    {legalMessage}
                </LegalMessage>
            </Footer>
        </Wrapper>
    );

};

CreationForm.propTypes = {
    reel: PropTypes.object.isRequired,
    lastModalNode: PropTypes.object.isRequired,
    validateReel: PropTypes.func.isRequired,
    createReel: PropTypes.func.isRequired,
    formState: PropTypes.object.isRequired,
    setFormState: PropTypes.func.isRequired,
};

export default CreationForm;
